"use client";

import React from "react";
import { useTranslations } from "next-intl";
import { FaChartBar, FaFilter, FaCalendarAlt, FaTags, FaDatabase } from "react-icons/fa";
import Prism from "prismjs";
import "prismjs/themes/prism-tomorrow.css";
import "prismjs/components/prism-typescript";

export default function AnalysisDocs() {
	const t = useTranslations('docs.analysis');

	React.useEffect(() => {
		Prism.highlightAll();
	}, []);

	const analysisFlowExample = `
// Main analysis function flow
export const analyze = async (
  author: { mtid: string; name: string; degrees: string[] },
  rawPublications: Publication[],
  translations?: Record<string, string>
): Promise<ProcessedPublicationData> => {
  // 1. Filter scientific publications
  const publications = rawPublications
    .map((data) => new Publication(data))
    .filter((pub) => pub.isScientific());
  
  // 2. Filter by authorship
  for (const pub of publications) {
    if (!pub.authorships?.some(
      (a) => a.author?.mtid === Number(author.mtid) && 
             a.authorTyped === true
    )) {
      pub.contributorOnly = true;
      continue; // Skip contributor-only publications
    }
    
    // 3. Load additional data
    loadNorwayLevel(pub);
    const impactFactor = await getImpactFactor(pub);
    const authorShare = pub.getAuthorShare(author.mtid);
    
    // 4. Calculate metrics
    const qScore = await pub.getQScore(authorShare, impactFactor);
    const iScoreData = pub.getIScore();
    
    // 5. Aggregate statistics
    stats.totalScientificPublications++;
    stats.qScore.fromArticles += qScore.qscore;
    // ... more aggregations
  }
  
  // 6. Calculate derived metrics
  stats.hIndex = calculateHIndex(citations);
  stats.qScore.total = stats.qScore.fromArticles + stats.qScore.fromBooks;
  
  return { statistics, chartData, authors, types, journals, publications, meta };
};`;

	const publicationFilteringExample = `
// Publication filtering logic
for (const pub of publications) {
  // Step 1: Must be scientific
  if (!pub.isScientific()) continue;
  
  // Step 2: Author must be typed author (not just contributor)
  if (!pub.authorships?.some(
    (a) => a.author?.mtid === Number(author.mtid) && 
           a.authorTyped === true
  )) {
    pub.contributorOnly = true;
    continue; // Exclude from statistics
  }
  
  // Publication passes all filters, process it
  stats.totalScientificPublications++;
}`;

	const yearlyAggregationExample = `
// Yearly data aggregation
const yearlyData: Record<string, {
  publications: number;
  iscore: number;
  qscore: number;
  publicationsWithIF: number;
  rankings: Record<string, number>;
}> = {};

for (const pub of publications) {
  const year = pub.publishedYear?.toString() || "Unknown";
  
  // Initialize year if not exists
  yearlyData[year] = yearlyData[year] || {
    publications: 0,
    iscore: 0,
    qscore: 0,
    publicationsWithIF: 0,
    rankings: { D1: 0, Q1: 0, Q2: 0, Q3: 0, Q4: 0, "N/A": 0 },
  };
  
  // Aggregate metrics
  yearlyData[year].publications++;
  yearlyData[year].iscore += pub.iscore || 0;
  yearlyData[year].qscore += pub.qscore || 0;
  
  if (impactFactor > 0 && qScore.qscore > 0) {
    yearlyData[year].publicationsWithIF++;
  }
  
  // Count by rating
  const rating = pub.getRating();
  yearlyData[year].rankings[rating]++;
}`;

	const publicationTypeExample = `
// Publication type categorization
const update_PublicationsByType_Stats = (
  pub: Publication,
  stats: Statistics
) => {
  const type = pub.type?.otypeName?.toLowerCase() || "";
  
  // Determine category (foreign, hungarianForeign, hungarianNative)
  const category = pub.foreignEdition
    ? "foreign"
    : pub.foreignLanguage
    ? "hungarianForeign"
    : "hungarianNative";
  
  // Map publication types
  const typeMap: Record<string, keyof Statistics["publicationsByType"]> = {
    journalarticle: "scientificArticle",
    bookchapter: pub.subType?.name?.toLowerCase().includes("konferencia")
      ? "conferenceArticle"
      : "bookChapter",
    book: "book",
    patent: "patent",
    thesis: "thesis",
  };
  
  const publicationType = typeMap[type] || "other";
  stats.publicationsByType[publicationType][category]++;
};`;

	const chartDataExample = `
// Chart data preparation from yearly data
const prepareChartDataFromYearlyData = (yearlyData) => {
  const years = Object.keys(yearlyData).sort();
  const cumulative = {
    D1: 0, Q1: 0, Q2: 0, Q3: 0, Q4: 0, NA: 0,
    iScore: 0, qScore: 0, publicationsWithIF: 0,
  };
  
  return {
    // Yearly rankings
    publicationsWithRankings: {
      years,
      rankings: {
        D1: years.map((year) => yearlyData[year].rankings.D1),
        Q1: years.map((year) => yearlyData[year].rankings.Q1),
        // ... Q2, Q3, Q4, N/A
      },
    },
    
    // Cumulative rankings
    cumulativePublications: {
      years,
      rankings: {
        D1: years.map((year) => (cumulative.D1 += yearlyData[year].rankings.D1)),
        // ... similar for other rankings
      },
    },
    
    // I Score and Q Score (yearly and cumulative)
    iScore: {
      label: "Éves független hivatkozások",
      years,
      data: years.map((year) => yearlyData[year].iscore),
    },
    cumulativeIScore: {
      label: "Kumulatív független hivatkozások",
      years,
      data: years.map((year) => (cumulative.iScore += yearlyData[year].iscore)),
    },
    // ... similar for Q Score and Impact Factor
  };
};`;

	return (
			<div className="prose prose-slate max-w-none prose-sm">
				<h1 className="flex items-center gap-3 text-2xl font-bold mb-4">
					<FaChartBar className="text-bme-main" />
					{t('title')}
				</h1>

				<p className="text-base text-gray-600 mb-6">
					{t('subtitle')}
				</p>

				{/* Analysis Flow Section */}
				<section id="analysis-flow" className="scroll-mt-20">
					<h2 className="text-3xl font-bold mt-12 mb-6 flex items-center gap-3">
						<FaDatabase className="text-bme-main" />
						{t('flow.title')}
					</h2>

					<p className="text-lg mb-4">
						{t('flow.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('flow.steps.title')}</h3>
					<ol className="list-decimal pl-6 mb-4 space-y-3">
						<li>{t('flow.steps.step1')}</li>
						<li>{t('flow.steps.step2')}</li>
						<li>{t('flow.steps.step3')}</li>
						<li>{t('flow.steps.step4')}</li>
						<li><strong>{t('flow.steps.step5')}</strong>
							<ul className="list-disc pl-6 mt-2">
								<li>{t('flow.steps.step5a')}</li>
								<li>{t('flow.steps.step5b')}</li>
								<li>{t('flow.steps.step5c')}</li>
							</ul>
						</li>
						<li><strong>{t('flow.steps.step6')}</strong>
							<ul className="list-disc pl-6 mt-2">
								<li>{t('flow.steps.step6a')}</li>
								<li>{t('flow.steps.step6b')}</li>
								<li>{t('flow.steps.step6c')}</li>
								<li>{t('flow.steps.step6d')}</li>
							</ul>
						</li>
						<li>{t('flow.steps.step7')}</li>
						<li>{t('flow.steps.step8')}</li>
						<li>{t('flow.steps.step9')}</li>
						<li>{t('flow.steps.step10')}</li>
					</ol>

					<pre className="!mt-4">
						<code className="language-typescript">{analysisFlowExample}</code>
					</pre>
				</section>

				{/* Publication Filtering Section */}
				<section id="publications" className="scroll-mt-20">
					<h2 className="text-3xl font-bold mt-12 mb-6 flex items-center gap-3">
						<FaFilter className="text-bme-main" />
						{t('filtering.title')}
					</h2>

					<p className="text-lg mb-4">
						{t('filtering.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('filtering.scientific.title')}</h3>
					<p className="mb-4">
						{t('filtering.scientific.description')}
					</p>
					<div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-6">
						<code className="text-blue-800">{t('filtering.scientific.condition')}</code>
					</div>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('filtering.authorship.title')}</h3>
					<p className="mb-4">
						{t('filtering.authorship.description')}
					</p>

					<div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg mb-6">
						<p className="text-yellow-800 m-0">
							<strong>{t('filtering.authorship.note')}</strong>
						</p>
					</div>

					<pre className="!mt-4">
						<code className="language-typescript">{publicationFilteringExample}</code>
					</pre>
				</section>

				{/* Yearly Aggregation Section */}
				<section id="trends" className="scroll-mt-20">
					<h2 className="text-3xl font-bold mt-12 mb-6 flex items-center gap-3">
						<FaCalendarAlt className="text-bme-main" />
						{t('yearly.title')}
					</h2>

					<p className="text-lg mb-4">
						{t('yearly.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('yearly.metrics.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('yearly.metrics.publications')}</strong></li>
						<li><strong>{t('yearly.metrics.iscore')}</strong></li>
						<li><strong>{t('yearly.metrics.qscore')}</strong></li>
						<li><strong>{t('yearly.metrics.publicationsWithIF')}</strong></li>
						<li><strong>{t('yearly.metrics.rankings')}</strong></li>
					</ul>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('yearly.unknownYear.title')}</h3>
					<p className="mb-4">
						{t('yearly.unknownYear.description')}
					</p>

					<pre className="!mt-4">
						<code className="language-typescript">{yearlyAggregationExample}</code>
					</pre>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('yearly.cumulative.title')}</h3>
					<p className="mb-4">
						{t('yearly.cumulative.description')}
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						{[0, 1, 2, 3].map((index) => {
							const item = t(`yearly.cumulative.item${index}` as any)
							return item ? <li key={index}>{item}</li> : null
						})}
					</ul>
					<p className="mb-4">
						{t('yearly.cumulative.note')}
					</p>
				</section>

				{/* Publication Type Categorization Section */}
				<section id="publication-types" className="scroll-mt-20">
					<h2 className="text-3xl font-bold mt-12 mb-6 flex items-center gap-3">
						<FaTags className="text-bme-main" />
						{t('types.title')}
					</h2>

					<p className="text-lg mb-4">
						{t('types.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('types.publicationTypes.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('types.publicationTypes.scientificArticle')}</strong></li>
						<li><strong>{t('types.publicationTypes.conferenceArticle')}</strong></li>
						<li><strong>{t('types.publicationTypes.bookChapter')}</strong></li>
						<li><strong>{t('types.publicationTypes.book')}</strong></li>
						<li><strong>{t('types.publicationTypes.patent')}</strong></li>
						<li><strong>{t('types.publicationTypes.thesis')}</strong></li>
						<li><strong>{t('types.publicationTypes.other')}</strong></li>
					</ul>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('types.categories.title')}</h3>
					<p className="mb-4">
						{t('types.categories.description')}
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('types.categories.foreign')}</strong></li>
						<li><strong>{t('types.categories.hungarianForeign')}</strong></li>
						<li><strong>{t('types.categories.hungarianNative')}</strong></li>
					</ul>

					<div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
						<h4 className="font-semibold mb-2">{t('types.logic.title')}</h4>
						<ol className="list-decimal pl-6 space-y-1">
							<li>{t('types.logic.step1')}</li>
							<li>{t('types.logic.step2')}</li>
							<li>{t('types.logic.step3')}</li>
						</ol>
					</div>

					<pre className="!mt-4">
						<code className="language-typescript">{publicationTypeExample}</code>
					</pre>
				</section>

				{/* Chart Data Preparation Section */}
				<section id="visualizations" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4">{t('charts.title')}</h2>

					<p className="text-lg mb-4">
						{t('charts.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('charts.structure.title')}</h3>
					<p className="mb-4">
						{t('charts.structure.description')}
					</p>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('charts.structure.publicationsWithRankings')}</strong></li>
						<li><strong>{t('charts.structure.cumulativePublications')}</strong></li>
						<li><strong>{t('charts.structure.iScore')}</strong></li>
						<li><strong>{t('charts.structure.cumulativeIScore')}</strong></li>
						<li><strong>{t('charts.structure.qScore')}</strong></li>
						<li><strong>{t('charts.structure.cumulativeQScore')}</strong></li>
						<li><strong>{t('charts.structure.impactFactor')}</strong></li>
						<li><strong>{t('charts.structure.cumulativeImpactFactor')}</strong></li>
					</ul>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('charts.sorting.title')}</h3>
					<p className="mb-4">
						{t('charts.sorting.description')}
					</p>

					<pre className="!mt-4">
						<code className="language-typescript">{chartDataExample}</code>
					</pre>
				</section>

				{/* Metadata Section */}
				<section id="metadata" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4">{t('metadata.title')}</h2>

					<p className="text-lg mb-4">
						{t('metadata.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('metadata.fields.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('metadata.fields.lastUpdated')}</strong></li>
						<li><strong>{t('metadata.fields.ifListVersionYear')}</strong></li>
						<li><strong>{t('metadata.fields.norwayListVersionYear')}</strong></li>
					</ul>

					<div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-lg mb-6">
						<p className="text-blue-800 m-0">
							<strong>{t('metadata.note')}</strong>
						</p>
					</div>
					<div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-lg mb-6">
						<p className="text-red-800 m-0">
							<strong>{t('metadata.norwayListNote')}</strong>
						</p>
					</div>
				</section>

				{/* Additional Collections Section */}
				<section id="collections" className="scroll-mt-20">
					<h2 className="text-xl font-bold mt-8 mb-4">{t('collections.title')}</h2>

					<p className="text-lg mb-4">
						{t('collections.description')}
					</p>

					<h3 className="text-lg font-semibold mt-6 mb-3">{t('collections.sets.title')}</h3>
					<ul className="list-disc pl-6 mb-4 space-y-2">
						<li><strong>{t('collections.sets.authors')}</strong></li>
						<li><strong>{t('collections.sets.types')}</strong></li>
						<li><strong>{t('collections.sets.journals')}</strong></li>
						<li><strong>{t('collections.sets.publications')}</strong></li>
					</ul>

					<p className="mb-4">
						{t('collections.note')}
					</p>
				</section>
			</div>
	);
}

